import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { compose } from 'recompose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import { connect as connectRedux } from 'react-redux'
import Link from '../Link/index'
import { getBoundActions } from '../../actions/index'
import {
  connect as connectPage,
  PAGE_SEO_TYPE_LOCATION,
} from '../Page/connect'
import { TYPE_SEARCH } from '../../services/tiles/index'
import {
  STORE_KEY_SEARCH,
} from '../../services/store-keys/index'
import SearchForm from '../SearchForm/index'
import TileGridV2 from '../TileGrid.v2/index'
import LoadMore from '../LoadMore/index'
import BackToTop from '../BackToTop/index'
import CommentsLoader from '../CommentsLoader/index'
import { connect as connectTiles } from '../Tiles/connect'
import { connect as connectLanguage } from '../Language/connect'
import {
  SCREEN_TYPE_SEARCH_PAGE,
  createUpstreamContext,
} from '../../services/upstream-context/index'

// Portals search removed — legacy feature no longer in use.
// Users in the PostHog experiment see AiGuideSearchPage via SearchPageSwitch.
// Everyone else sees this page (simple grid + sort + load more).

function createOnLanguageChange (actions) {
  return actions.tiles.resetTilesData
}

function getTilesState (storeKey, props) {
  const { tiles } = props
  return tiles.get(storeKey, Map())
}

function renderTileGrid (tileStoreKey, props, rowId) {
  const tilesState = getTilesState(tileStoreKey, props)
  const tileGridData = tilesState.getIn(['data', 'titles'], List())
  if (!tilesState.get('data')) {
    return <div className="search__tile-grid-placeholder" />
  }

  if (tileGridData && tileGridData.size < 1) {
    return null
  }

  return (
    <TileGridV2
      tileData={tileGridData}
      adminTitle={'Search Page'}
      upstreamContext={createUpstreamContext({
        screenType: SCREEN_TYPE_SEARCH_PAGE,
        screenParam: tilesState.get('id'),
        storeKey: rowId,
      })}
      uiLocation="Search"
    />
  )
}

function renderLoadMore (props) {
  const { getTilesDataLoadMore } = props
  const tilesState = getTilesState(STORE_KEY_SEARCH, props)
  const titlesCount = tilesState.getIn(['data', 'titles'], List()).size
  const totalTilesCount = tilesState.getIn(['data', 'totalCount'], 0)
  const processing = tilesState.get('processing')
  return (
    <LoadMore
      onLoadMore={getTilesDataLoadMore}
      buttonClassName={['search__load-more-button', 'button--secondary']}
      currentCount={titlesCount}
      totalCount={totalTilesCount}
      processing={processing}
      infiniteScroll
    />
  )
}

function renderSearchContent (props) {
  return (
    <div className="search__search-content">
      <div className="search__results-container">
        {renderTileGrid(STORE_KEY_SEARCH, props, STORE_KEY_SEARCH)}
      </div>
    </div>
  )
}

function renderSearchBottom (props) {
  return (
    <div className="search__bottom">
      {renderLoadMore(props)}
      <BackToTop />
    </div>
  )
}

function getMessage (tiles, auth) {
  //
  const messages = tiles.getIn(['data', 'messages'], List())

  if (!messages || !messages.size) {
    return null
  }

  let message = messages.get(0)

  if (messages.size > 1) {
    const uniqueMessage = messages.find((m) => m.get('type') !== 'no-results')
    const defaultMessage = messages.find((m) => m.get('type') === 'no-results')
    message = auth.get('jwt') ? (uniqueMessage || defaultMessage) : defaultMessage
  }

  if (!message) {
    return null
  }

  const url = message.get('url', '')
  const text = message.get('text', '')
  const isAbsolutePath = /^http/.test(url)
  const urlLabel = message.get('urlLabel')
  const linkProps = { to: url }

  if (isAbsolutePath) {
    linkProps.target = '_blank'
    linkProps.directLink = true
  }

  return (
    <p>
      {text}
      {text ? <br /> : null}
      {url ? (
        <Link {...linkProps}>
          {urlLabel}
        </Link>
      ) : null}
    </p>
  )
}

class SearchPage extends Component {
  //
  constructor (props) {
    super(props)
    this.state = {}
  }

  componentWillUnmount () {
    const { setCommentsVisible } = this.props
    setCommentsVisible(false)
  }

  renderLegacySearch () {
    const { props } = this
    const { location, auth } = props
    const q = _get(location, ['query', 'q'])
    const tilesState = getTilesState(STORE_KEY_SEARCH, props)
    const titles = tilesState.getIn(['data', 'titles'], List())
    const message = getMessage(tilesState, auth)

    return (
      <div className="search__body">
        {message}
        {!message && q && titles && titles.size > 0 ? renderSearchContent(props) : null}
        {!message && q && titles && titles.size > 0 ? renderSearchBottom(props) : null}
      </div>
    )
  }

  render () {
    const { props } = this
    const {
      history,
      location,
    } = props
    const q = _get(location, ['query', 'q'])
    const sort = _get(location, ['query', 'sort'], '')
    return (
      <section className="search">
        <CommentsLoader />
        <SearchForm
          autofocus
          className={['search__search-form']}
          hasButton
          location={location}
          defaultSearchTerm={q}
          defaultSearchSort={sort}
          history={history}
          iconClass={['search__search-icon']}
        />
        {this.renderLegacySearch()}
      </section>
    )
  }
}

SearchPage.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  tiles: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  page: ImmutablePropTypes.map.isRequired,
  staticTextSearchPage: ImmutablePropTypes.map.isRequired,
  setPageSeo: PropTypes.func.isRequired,
  setCommentsVisible: PropTypes.func.isRequired,
  getTilesDataLoadMore: PropTypes.func.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectLanguage({ createOnLanguageChange }),
  connectRedux(
    (state) => {
      const storeKeyList = [STORE_KEY_SEARCH]
      const tiles = state.tiles.filter((val, key) => _includes(storeKeyList, key) || key === 'scrollableRowWidth')
      return {
        tiles,
        auth: state.auth,
        page: state.page,
        staticTextSearchPage: state.staticText.getIn(
          ['data', 'searchPage'],
          Map(),
        ),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPageSeo: actions.page.setPageSeo,
        setCommentsVisible: actions.comments.setCommentsVisible,
      }
    },
  ),
  connectTiles({
    storeKey: STORE_KEY_SEARCH,
    type: TYPE_SEARCH,
    queryParamAsId: 'q',
    ignoreEmptyId: true,
    enablePageBehaviors: true,
  }),
)(SearchPage)
